//#region Imports

import { Component, ElementRef, forwardRef, HostListener, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { IconComponent } from '../icon/icon.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { SpinnerColorEnum } from '../spinner/types/spinner-color.enum';
import { SpinnerSizeEnum } from '../spinner/types/spinner-size.enum';
import { Tag } from '../../models/interfaces/tag.interface';
import { COURSE_TAGS_MAX, normalizeTagName, TAG_NAME_MAX_LENGTH, TagService } from '../../services/tag/tag.service';
import { PermissionService } from '../../services/permission/permission.service';
import { ModalService } from '../../services/modal/modal.service';
import { ToastService } from '../toast/service/toast.service';
import { PermissionEnum, ResourceEnum } from '../../../../environments/shared-environment/permission';
import { CreateTagModalComponent, CreateTagModalData } from '../../../pages/tag/modals/create-tag-modal.component';
import { getCrudErrorMessage } from '../../utils/functions';

//#endregion

@Component({
  selector: 'l-tag-select',
  standalone: true,
  templateUrl: './tag-select.component.html',
  imports: [IconComponent, SpinnerComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagSelectComponent),
      multi: true,
    },
  ],
})
export class TagSelectComponent implements ControlValueAccessor, OnDestroy {

  //#region Constructor

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly tagService: TagService,
    private readonly permissionService: PermissionService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
  ) {
    this.search$$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$$),
    ).subscribe((term) => {
      void this.loadOptions(term);
    });
  }

  //#endregion

  //#region Public Properties

  @Input()
  public inputId = 'tagSelect';

  @Input()
  public placeholder = 'Selecione as tags de pesquisa';

  public readonly maxTags = COURSE_TAGS_MAX;

  public readonly spinnerColorEnum = SpinnerColorEnum;

  public readonly spinnerSizeEnum = SpinnerSizeEnum;

  public selected: Tag[] = [];

  public options: Tag[] = [];

  public term = '';

  public isOpen = false;

  public isLoading = false;

  public loadErrorMessage = '';

  public isDisabled = false;

  //#endregion

  //#region Private Properties

  private readonly newTagIds = new Set<string>();

  private readonly search$$ = new Subject<string>();

  private readonly destroy$$ = new Subject<void>();

  private hasLoadedOptions = false;

  private loadRequestId = 0;

  //#endregion

  //#region Getters

  public get canCreate(): boolean {
    return this.permissionService.has(ResourceEnum.TAGS, PermissionEnum.CREATE);
  }

  public get trimmedTerm(): string {
    return this.term.trim();
  }

  public get hasExactMatch(): boolean {
    const normalized = normalizeTagName(this.term);
    return this.options.some((tag) => tag.name === normalized);
  }

  public get hasReachedLimit(): boolean {
    return this.selected.length >= this.maxTags;
  }

  public get showEmptySearch(): boolean {
    return !this.isLoading && !this.loadErrorMessage && this.options.length === 0 && !!this.trimmedTerm;
  }

  public get showEmptyList(): boolean {
    return !this.isLoading && !this.loadErrorMessage && this.options.length === 0 && !this.trimmedTerm;
  }

  public get createLabel(): string {
    return this.trimmedTerm && !this.hasExactMatch ? `Criar tag "${this.trimmedTerm}"` : 'Criar nova tag';
  }

  //#endregion

  //#region ControlValueAccessor

  public writeValue(value: Tag[] | null): void {
    this.selected = Array.isArray(value) ? [...value] : [];
  }

  public registerOnChange(fn: (value: Tag[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (isDisabled) {
      this.close();
    }
  }

  //#endregion

  //#region Lifecycle Hooks

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  //#endregion

  //#region Public Methods

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  public open(): void {
    if (this.isDisabled || this.isOpen) {
      return;
    }

    this.isOpen = true;

    if (!this.hasLoadedOptions) {
      void this.loadOptions(this.term);
    }
  }

  public toggle(): void {
    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  public close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.onTouched();
  }

  public onInput(value: string): void {
    this.term = value.slice(0, TAG_NAME_MAX_LENGTH);
    this.open();
    this.search$$.next(this.term);
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    const normalized = normalizeTagName(this.term);
    const match = this.options.find((tag) => tag.name === normalized);

    if (match) {
      this.select(match);
      return;
    }

    if (normalized && this.canCreate) {
      this.openCreateModal();
    }
  }

  public isSelected(tag: Tag): boolean {
    return this.selected.some((item) => item.id === tag.id);
  }

  public isNew(tag: Tag): boolean {
    return this.newTagIds.has(tag.id);
  }

  public onOptionClick(tag: Tag): void {
    if (this.isSelected(tag)) {
      this.remove(tag);
      return;
    }

    this.select(tag);
  }

  public remove(tag: Tag): void {
    if (this.isDisabled) {
      return;
    }

    this.setSelected(this.selected.filter((item) => item.id !== tag.id));
  }

  public retry(): void {
    void this.loadOptions(this.term);
  }

  public openCreateModal(): void {
    if (!this.canCreate || this.hasReachedLimit) {
      return;
    }

    const initialName = this.hasExactMatch ? '' : this.trimmedTerm;
    this.close();

    const data: CreateTagModalData = { initialName, addToCourse: true };
    const ref = this.modalService.open(CreateTagModalComponent, {
      width: '700px',
      modal: true,
      closable: false,
      showHeader: false,
      data,
    });

    ref.onClose.pipe(takeUntil(this.destroy$$)).subscribe((created?: Tag) => {
      if (!created) {
        return;
      }

      this.newTagIds.add(created.id);
      this.options = [created, ...this.options.filter((tag) => tag.id !== created.id)];
      this.term = '';
      this.hasLoadedOptions = false;
      this.select(created);
      this.toastService.success(`Tag "${created.name}" criada e adicionada ao curso.`);
    });
  }

  public trackById(_: number, tag: Tag): string {
    return tag.id;
  }

  //#endregion

  //#region Private Methods

  private onChange: (value: Tag[]) => void = () => undefined;

  private onTouched: () => void = () => undefined;

  private select(tag: Tag): void {
    if (this.isSelected(tag)) {
      return;
    }

    if (this.hasReachedLimit) {
      this.toastService.warn(`Um curso pode ter no máximo ${this.maxTags} tags.`, 'Limite de tags');
      return;
    }

    this.setSelected([...this.selected, tag]);
  }

  private setSelected(tags: Tag[]): void {
    this.selected = tags;
    this.onChange([...tags]);
    this.onTouched();
  }

  private async loadOptions(term: string): Promise<void> {
    const requestId = ++this.loadRequestId;

    try {
      this.isLoading = true;
      this.loadErrorMessage = '';

      const options = await this.tagService.searchActive(term);

      // Descarta respostas de buscas antigas
      if (requestId !== this.loadRequestId) {
        return;
      }

      this.options = options;
      this.hasLoadedOptions = !term.trim();
    } catch (error) {
      if (requestId === this.loadRequestId) {
        this.options = [];
        this.loadErrorMessage = getCrudErrorMessage(error, 'Não foi possível carregar as tags.');
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading = false;
      }
    }
  }

  //#endregion

}
